// PrivacyAgreementQuestion.tsx
import { useState } from 'react';
import {
    Box,
    Typography,
    Checkbox,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Grid, DialogContent, IconButton, Dialog,
    Link
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React from 'react';
import { updateUserData } from "../../store/questionnaireSlice.ts";
import { useStoreDispatch } from "../../store/hooks.ts";
import { Close, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import InfoPopupContent from "./InfoPopupContent.tsx";
import { QuestionConfig } from "../../data/QuestionConfig.ts";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

interface PrivacyAgreementQuestionProps {
    onNameChange: (name: string) => void;
    onSubscribeNewsletterChange: (subscribeNewsletter: boolean) => void;
    onEmailChange: (email: string) => void;
    onRegionChange: (region: string) => void;
    onAgreePrivacyChange: (agreePrivacy: boolean) => void;
    questionConfig: QuestionConfig;
}

export default function PrivacyAgreementQuestion({
                                                     onNameChange,
                                                     onSubscribeNewsletterChange,
                                                     onEmailChange,
                                                     onRegionChange,
                                                     onAgreePrivacyChange,
                                                     questionConfig
                                                 }: PrivacyAgreementQuestionProps) {
    const { t, i18n } = useTranslation();
    const dispatch = useStoreDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [region, setRegion] = useState('');
    const [agreePrivacy, setAgreePrivacy] = useState(false);
    const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
    const [showFullText, setShowFullText] = useState(false);
    const [showFullNewsletterText, setShowFullNewsletterText] = useState(false);

    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const toggleShowFullText = () => setShowFullText(!showFullText);
    const toggleShowFullNewsletterText = () => setShowFullNewsletterText(!showFullNewsletterText);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setName(value);
        onNameChange(value);

        // Namen im Store unter "name" speichern
        dispatch(updateUserData({ field: "name", value }));
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setEmail(value);
        onEmailChange(value);

        // E-Mail-Formatprüfung
        if (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
            setEmailError(t('email_validation_error'));
        } else {
            setEmailError('');
        }

        // E-Mail im Store unter "email" speichern
        dispatch(updateUserData({ field: "email", value }));
    };

    const handleRegionChange = (event: { target: { value: string; }; }) => {
        setRegion(event.target.value as string);
        onRegionChange(event.target.value as string);
    };

    const handleAgreePrivacyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgreePrivacy(event.target.checked);
        onAgreePrivacyChange(event.target.checked);
    };

    const handleSubscribeNewsletterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubscribeNewsletter(event.target.checked);
        onSubscribeNewsletterChange(event.target.checked);
    };

    // Funktion zum Rendern des verlinkten Textes
    const renderLinkedText = (text: string, linkWord: string) => {
        if (text.includes(linkWord)) {
            const parts = text.split(linkWord);
            return (
                <>
                    {parts[0]}
                    <Link
                        onClick={handleOpen}
                        underline="always"
                        sx={{
                            cursor: 'pointer',
                            color: theme.palette.primary.main,
                            fontWeight: 'bold',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {linkWord}
                    </Link>
                    {parts[1]}
                </>
            );
        }
        return text;
    };

    // Intelligente Textkürzung für den ersten Satz
    const shortenText = (text: string) => {
        const currentLanguage = i18n.language;
        // Die ersten Sätze, die vollständig angezeigt werden sollen
        const firstSentences = {
            de: "Ich stimme der Einwilligungserklärung zur Verarbeitung der Daten zu meinen gesundheitlichen Angaben zu",
            en: "I agree to the declaration of consent for the processing of my health data"
        };

        const firstSentence = currentLanguage.startsWith('de') ? firstSentences.de : firstSentences.en;

        // Versuche, den ersten Satz im Text zu finden
        const sentenceIndex = text.indexOf(firstSentence);

        if (sentenceIndex !== -1) {
            // Finde das Ende des ersten Satzes
            const endIndex = sentenceIndex + firstSentence.length;

            // Prüfe, ob nach dem Satz ein Klammertext folgt
            const restOfText = text.substring(endIndex);
            const bracketMatch = restOfText.match(/^\s*\([^)]+\)/);

            if (bracketMatch) {
                // Zeige den ersten Satz und den Klammertext an
                return text.substring(0, endIndex + bracketMatch[0].length) + "...";
            } else {
                // Zeige nur den ersten Satz an
                return text.substring(0, endIndex) + "...";
            }
        }

        // Fallback 1: Versuche, nach einem Zeilenumbruch zu kürzen
        const newlineIndex = text.indexOf('\n');
        if (newlineIndex !== -1) {
            return text.substring(0, newlineIndex) + "...";
        }

        // Fallback 2: Kürze den Text bei ca. 1/3 der Gesamtlänge
        const maxLength = Math.min(text.length / 3, 120);
        return text.substring(0, maxLength) + "...";
    };

    // Textkürzung für den Newsletter-Text
    const shortenNewsletterText = (text: string) => {
        const currentLanguage = i18n.language;
        const newsletterWords = {
            de: "abonnieren",
            en: "newsletter"
        };

        const newsletterWord = currentLanguage.startsWith('de') ? newsletterWords.de : newsletterWords.en;

        // Suche nach dem Wort "abonnieren"/"newsletter" und schneide kurz danach ab
        const wordIndex = text.toLowerCase().indexOf(newsletterWord.toLowerCase());

        if (wordIndex !== -1) {
            // Finde das Ende des Satzes nach dem Wort oder schneide nach 20 Zeichen
            const endIndex = wordIndex + newsletterWord.length;
            const nextPeriod = text.indexOf('.', endIndex);

            if (nextPeriod !== -1 && nextPeriod < endIndex + 30) {
                return text.substring(0, nextPeriod + 1) + "...";
            } else {
                return text.substring(0, endIndex + 20) + "...";
            }
        }

        // Fallback: Kürze bei 1/2 der Länge
        return text.substring(0, text.length / 2) + "...";
    };

    // Funktion zum Rendern des Datenschutztexts mit Pfeil nach unten/oben
    const renderPrivacyText = () => {
        const fullText = t("privacy_consent_processing");
        const privacyPolicyWord = t("privacy_policy_term");

        // Für mobile Geräte und wenn der vollständige Text nicht angezeigt werden soll
        if (isMobile && !showFullText) {
            // Kürze den Text für mobile Ansicht intelligent
            const shortenedText = shortenText(fullText);
            return (
                <>
                    {renderLinkedText(shortenedText, privacyPolicyWord)}
                    <IconButton
                        onClick={toggleShowFullText}
                        color="primary"
                        size="small"
                        sx={{ ml: 0.5, p: '2px' }}
                    >
                        <KeyboardArrowDown fontSize="small" />
                    </IconButton>
                </>
            );
        }

        // Für mobile Geräte mit angezeigtem vollständigem Text
        if (isMobile && showFullText) {
            return (
                <>
                    {renderLinkedText(fullText, privacyPolicyWord)}
                    <IconButton
                        onClick={toggleShowFullText}
                        color="primary"
                        size="small"
                        sx={{ ml: 0.5, p: '2px' }}
                    >
                        <KeyboardArrowUp fontSize="small" />
                    </IconButton>
                </>
            );
        }

        // Für Desktop immer den vollständigen Text mit Link anzeigen
        return renderLinkedText(fullText, privacyPolicyWord);
    };

    // Funktion zum Rendern des Newsletter-Consent-Texts mit klickbarem Datenschutz-Link
    const renderNewsletterConsentText = () => {
        const newsletterText = t("newsletter_consent");
        const privacyPolicyWord = t("privacy_policy_term");

        // Für mobile Geräte und wenn der vollständige Text nicht angezeigt werden soll
        if (isMobile && !showFullNewsletterText) {
            // Kürze den Newsletter-Text
            const shortenedText = shortenNewsletterText(newsletterText);
            return (
                <>
                    {renderLinkedText(shortenedText, privacyPolicyWord)}
                    <IconButton
                        onClick={toggleShowFullNewsletterText}
                        color="primary"
                        size="small"
                        sx={{ ml: 0.5, p: '2px' }}
                    >
                        <KeyboardArrowDown fontSize="small" />
                    </IconButton>
                </>
            );
        }

        // Für mobile Geräte mit angezeigtem vollständigem Text
        if (isMobile && showFullNewsletterText) {
            return (
                <>
                    {renderLinkedText(newsletterText, privacyPolicyWord)}
                    <IconButton
                        onClick={toggleShowFullNewsletterText}
                        color="primary"
                        size="small"
                        sx={{ ml: 0.5, p: '2px' }}
                    >
                        <KeyboardArrowUp fontSize="small" />
                    </IconButton>
                </>
            );
        }

        // Für Desktop immer den vollständigen Text anzeigen
        return renderLinkedText(newsletterText, privacyPolicyWord);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                my: 2, // Abstand reduziert (von my: 3)
            }}
        >

            <Box>
                <Box display="flex" alignItems="flex-start">
                    <Checkbox
                        checked={agreePrivacy}
                        onChange={handleAgreePrivacyChange}
                        name="agreePrivacy"
                    />
                    <Typography sx={{ textAlign: 'left' }}>
                        {renderPrivacyText()}
                    </Typography>
                </Box>
            </Box>

            <Typography variant="body1" mb={2} mt={3} gutterBottom>
                {t("product_recommendations_email_question")}
            </Typography>

            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={t("name_label")}
                        value={name}
                        onChange={handleNameChange}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label={t("email_label")}
                        value={email}
                        onChange={handleEmailChange}
                        error={Boolean(emailError)}
                        helperText={emailError}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel>{t("region_language_label")}</InputLabel>
                        <Select
                            value={region}
                            onChange={handleRegionChange}
                            label={t("region_language_label")}
                        >
                            <MenuItem value="EU/DE">{t("region_eu_de")}</MenuItem>
                            <MenuItem value="EU/EN">{t("region_eu_en")}</MenuItem>
                            <MenuItem value="US/EN">{t("region_us_en")}</MenuItem>
                            <MenuItem value="CH/DE">{t("region_ch_de")}</MenuItem>
                            <MenuItem value="CH/EN">{t("region_ch_en")}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Box mt={3} display="flex" flexDirection="column">

                <Box display="flex" alignItems="center" mt={4}>
                    <Checkbox
                        checked={subscribeNewsletter}
                        onChange={handleSubscribeNewsletterChange}
                        name="subscribeNewsletter"
                    />
                    <Typography sx={{ textAlign: 'left' }}>
                        {renderNewsletterConsentText()}
                    </Typography>
                </Box>
            </Box>

            <Dialog open={open} onClose={handleClose}
                    maxWidth="xl"
                    fullWidth
            >
                <DialogContent
                    sx={{
                        padding: 3,
                        maxHeight: '80vh',
                        width: '100%',
                        overflow: 'auto'
                    }}
                >
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6">{t('info_dialog_title')}</Typography>
                        <IconButton onClick={handleClose} aria-label={t('info_dialog_close')}>
                            <Close />
                        </IconButton>
                    </Box>

                    <InfoPopupContent questionConfig={questionConfig} />

                </DialogContent>
            </Dialog>
        </Box>
    );
}
